import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { EmployeesValueObjectsEmployeeSelectorTypeEnumEnum } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateTrainingClassMembership } from 'modules/trainings/hooks/TrainingClassMemberships/useCreateTrainingClassMembership'
import { useTrainingClasses } from 'modules/trainings/hooks/TrainingClasses/useTrainingClasses'

interface EmployeeSelection {
  type: EmployeesValueObjectsEmployeeSelectorTypeEnumEnum
  excludedIds?: number[]
}

interface ParticipantFilters {
  search?: string
  status?: string
  teamId?: number
  classId?: number
  dueDate?: string
}

interface Props {
  accessIds?: number[]
  selection?: EmployeeSelection
  filters?: ParticipantFilters
}

export const AddToClassModal = ({ accessIds, selection, filters }: Props) => {
  const [classIds, setClassIds] = useState<number[]>([])
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { createTrainingClassMemberships } = useCreateTrainingClassMembership()
  const { id } = useParams()
  const { trainingClasses } = useTrainingClasses({ trainingId: Number(id) })
  const { company } = useCurrentSession()

  const onSubmit = async () => {
    const response = await createTrainingClassMemberships({
      trainingClassIds: classIds,
      ...(selection ? { selection } : { accessIds }),
      ...filters,
      companyId: company.id,
    })

    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.assign_modal.error'
        ),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.assign_modal.success'
        ),
      })

      navigate(
        navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
      )
    }
  }

  return (
    <Modal
      title={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.assign_modal.title'
      )}
      description={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.assign_modal.reviewed_description'
      )}
      onClose={() =>
        navigate(
          navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
        )
      }
      footer={
        <Box gap="s8" flexDirection="row">
          <Button.Router
            to={{
              pathname: navigation.trainings.list.details_revamp.view.participants.fullPath({
                id: Number(id),
              }),
            }}
            type="secondary"
          >
            {i18n.t('common.cancel')}
          </Button.Router>
          <Button onClick={() => onSubmit()} type="negative">
            {i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.assign_modal.assign'
            )}
          </Button>
        </Box>
      }
    >
      <Selector.Popover
        label={i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.assign_modal.classes'
        )}
        placeholder={i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.assign_modal.placeholder'
        )}
        searchPlaceholder={i18n.t('common.search')}
        blankLabel=""
        value={classIds}
        onChange={(classSelection) => {
          // This is bonkers, but I can't find where are we passing numbers created with the constructor
          // For the uninitiated on JS sourcery: 'new Number' is of type 'object', instead of type 'number'.
          //
          // So we check if we're receiving an array - cause the component can also return single items somehow?
          //
          // And also if the values are numbers in the most inclusive way possible.
          //
          // I've checked some helper libraries (lodash) and not even them cover this :grimmacing:
          if (
            !Array.isArray(classSelection) ||
            classSelection.some((item) => typeof item !== 'number' && !(item instanceof Number))
          ) {
            throw new Error(
              `Invalid value for popover selection. Expected a number array. Got: ${classSelection} of type ${typeof classSelection}`
            )
          }
          setClassIds(classSelection.map((item) => Number(item)))
        }}
        isMultiSelect
      >
        {trainingClasses.map((trainingClass, index) => (
          <Selector.Option
            key={index}
            value={Number(trainingClass.id)}
            label={trainingClass.name}
            searchValue={trainingClass.name}
          >
            {trainingClass.name}
          </Selector.Option>
        ))}
      </Selector.Popover>
    </Modal>
  )
}
